"use client";

import { useState, useRef, useEffect } from "react";
import { isFinished, doneOf } from "./onboarding-progress";
import { steps, type StepKey } from "./onboarding-steps";
import type { OnboardingProgress } from "./onboarding-progress";
import { reduceQuery } from "./use-reduced-motion";

export function useOnboardingProgress({
  progress,
  pollBroken,
}: Readonly<{
  progress: OnboardingProgress;
  pollBroken: boolean;
}>) {
  const [retryVisible, setRetryVisible] = useState(pollBroken);

  if (pollBroken && !retryVisible) setRetryVisible(true);

  const finished = isFinished(progress);
  const activeStep = steps.find((step) => !doneOf({ key: step.key, progress }));
  const activeIndex = activeStep ? steps.indexOf(activeStep) : -1;
  const visibleSteps =
    activeIndex === -1 ? steps : steps.slice(0, activeIndex + 1);
  const liveRowRef = useRef<HTMLLIElement | null>(null);
  const liveKey = `${visibleSteps.length}:${activeStep?.key ?? "done"}`;
  const lastScrolledKey = useRef(liveKey);
  useEffect(() => {
    if (lastScrolledKey.current === liveKey) return;

    lastScrolledKey.current = liveKey;
    liveRowRef.current?.scrollIntoView({
      behavior: window.matchMedia(reduceQuery).matches ? "auto" : "smooth",
      block: "nearest",
    });
  }, [liveKey]);
  const [transition, setTransition] = useState<
    Readonly<{
      progress: OnboardingProgress;
      changed: ReadonlySet<StepKey>;
      announcement: string;
    }>
  >({ progress, changed: new Set(), announcement: "" });

  if (progress !== transition.progress) {
    const completed = steps.filter(
      (step) =>
        !doneOf({ key: step.key, progress: transition.progress }) &&
        doneOf({ key: step.key, progress }),
    );
    const changed = new Set(completed.map((step) => step.key));
    const lastCompleted = completed.at(-1);
    const announcement = lastCompleted
      ? `${lastCompleted.title} done`
      : transition.announcement;

    if (changed.size > 0 && activeStep) changed.add(activeStep.key);

    setTransition({ progress, changed, announcement });
  }

  const recentlyChanged = transition.changed;
  useEffect(() => {
    if (recentlyChanged.size === 0) return;

    const id = window.setTimeout(
      () => setTransition((current) => ({ ...current, changed: new Set() })),
      300,
    );
    return () => window.clearTimeout(id);
  }, [recentlyChanged]);
  return {
    retryVisible,
    finished,
    activeIndex,
    visibleSteps,
    liveRowRef,
    recentlyChanged,
    announcement: transition.announcement,
  };
}
